<template>
  <div class="row">
    <div class="col-md-12">
      <hr />
      <h2 class="card-title">Product Matrix</h2>
      <p class="ml-3 mb-3">Configure variants for this product</p>
      <base-alert v-if="errors.length && error_count > 0" type="danger">
        <b>You have the following error(s):</b>
        <li v-for="error in errors" v-bind:key="error">{{ error }}</li>
      </base-alert>
      <base-alert v-else-if="errors.length && error_count == 0" type="success">
        <b v-for="error in errors" v-bind:key="error">{{ error }}</b>
      </base-alert>

      <a-tabs :activeKey="active_key" @tabClick="callback">
        <a-tab-pane key="1">
          <span slot="tab">
            <span class="numberCircle">1</span>
            Add Variants
          </span>
          Enter the variants available for this product
          <div class="row">
            <div class="col-md-3">
              <h3>Variants</h3>
            </div>
            <div class="col-md-9">
              <h3>Options</h3>
            </div>
          </div>

          <div class="row border-bottom mb-3">
            <div class="col-md-3">
              <p class="border rounded px-1">Design</p>
            </div>
            <div class="col-md-9">
              <a-select
                mode="multiple"
                v-model="design_id"
                showSearch
                placeholder="Select Design"
                optionFilterProp="children"
                style="width: 100%"
                :filterOption="filterOption"
              >
                <a-select-option
                  v-for="option in designOptions"
                  :key="option.id"
                  :value="option.id"
                  >{{ option.name }}</a-select-option
                >
              </a-select>
            </div>
          </div>

          <div class="row border-bottom mb-3">
            <div class="col-md-3">
              <p class="border rounded px-1">Color</p>
            </div>
            <div class="col-md-9">
              <a-select
                mode="multiple"
                v-model="color_id"
                showSearch
                placeholder="Select Color"
                optionFilterProp="children"
                style="width: 100%"
                :filterOption="filterOption"
              >
                <a-select-option
                  v-for="option in colorOptions"
                  :key="option.id"
                  :value="option.id"
                  >{{ option.name }}</a-select-option
                >
              </a-select>
            </div>
          </div>

          <div class="row">
            <div class="col-md-3">
              <p class="border rounded px-1">Size</p>
            </div>
            <div class="col-md-9">
              <div class="row mb-3">
                <div class="col-md-5">
                  <a-select
                    v-model="size_group_id"
                    showSearch
                    placeholder="Select Size Group"
                    optionFilterProp="children"
                    style="width: 100%"
                    :filterOption="filterOption"
                    @change="getSizes"
                  >
                    <a-select-option
                      v-for="option in sizeGroupOptions"
                      :key="option.id"
                      :value="option.id"
                      >{{ option.name }}</a-select-option
                    >
                  </a-select>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-12">
                  <a-select
                    mode="multiple"
                    v-model="size_id"
                    showSearch
                    placeholder="Select Size"
                    optionFilterProp="children"
                    style="width: 100%"
                    :filterOption="filterOption"
                  >
                    <a-select-option
                      v-for="option in sizeOptions"
                      :key="option.id"
                      :value="option.id"
                      >{{ option.name }}</a-select-option
                    >
                  </a-select>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
        <a-tab-pane key="2">
          <span slot="tab">
            <span class="numberCircle">2</span>
            Filter Variants
          </span>
          Select which variant combinations will be applied to this product

          <div class="row">
            <div class="col-md-8">
              <a-table
                :rowSelection="{
                  selectedRowKeys: selectedRowKeys,
                  onChange: onSelectChange,
                  getCheckboxProps: (record) => ({
                    props: { disabled: record.old_record == true },
                  }),
                }"
                :columns="columns"
                :dataSource="data"
                :pagination="false"
                :scroll="{ y: 400 }"
              />
            </div>
          </div>
        </a-tab-pane>
        <a-tab-pane key="3">
          <span slot="tab">
            <span class="numberCircle">3</span>
            Configure Variants
          </span>
          Add barcode, cost, price information to each variant combination
          <a-table :columns="matrixColumns" :dataSource="matrixData" :pagination="false" :scroll="{ y: 400 }">
            <template v-for="col in ['reference_code','barcode','landed_cost', 'ordering_cost', 'retail_price']" :slot="col" slot-scope="text, record">
              <div :key="col">
                <a-input-group compact>
                  <a-input
                    v-if="col == 'barcode'"
                    style="margin: -5px 0"
                    :value="text"
                    @change="
                      (e) => handleChange(e.target.value, record.key, col)
                    "
                  />
                  <a-input
                    v-else-if="col == 'reference_code'"
                    style="margin: -5px 0"
                    :value="text"
                    @change="
                      (e) => handleChange(e.target.value, record.key, col)
                    "
                  />
                  <a-input
                    v-else
                    style="margin: -5px 0"
                    type="number"
                    :value="text"
                    @change="
                      (e) => handleChange(e.target.value, record.key, col)
                    "
                  />
                </a-input-group>
              </div>
            </template>
            <template slot="operation" slot-scope="text, record">
              <div class="editable-row-operations">
                <a-dropdown>
                  <a class="ant-dropdown-link">
                    <span class="fa fa-ellipsis-v"></span>
                  </a>
                  <a-menu slot="overlay">
                    <a-menu-item key="1" v-if="matrixData.length">
                      <a-popconfirm
                        title="Sure to delete?"
                        @confirm="() => matrixDelete(record.key)"
                      >
                        <a href="javascript:;">Delete</a>
                      </a-popconfirm>
                    </a-menu-item>
                  </a-menu>
                </a-dropdown>
              </div>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>

      <div class="row">
        <div class="col-md-12">
          <br />
          <button
            v-if="current < 3"
            type="button"
            class="btn btn-sm btn-default float-right ml-3"
            style="border-radius: 24px; border: 0px"
            @click.prevent="next"
          >
            Next
          </button>
          <button
            v-if="current == 3"
            type="button"
            class="btn btn-sm btn-default float-right ml-3"
            style="border-radius: 24px; border: 0px"
            @click.prevent="done"
          >
            Done
          </button>
          <button
            v-if="current > 1"
            type="button"
            class="btn btn-sm btn-default float-right ml-3"
            style="border-radius: 24px; border: 0px"
            @click.prevent="prev"
          >
            Previous
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import HTTPRequest from "../../controllers/httpRequest.js";
import { mapState } from "vuex";

const columns = [
  {
    title: "DESIGN",
    dataIndex: "design_name",
  },
  {
    title: "COLOR",
    dataIndex: "color_name",
  },
  {
    title: "SIZE",
    dataIndex: "size_name",
  },
];

const matrixColumns = [
  {
    title: "DESIGN",
    dataIndex: "design_name",
    width: "15%",
    scopedSlots: { customRender: "design_name" },
  },
  {
    title: "COLOR",
    dataIndex: "color_name",
    width: "15%",
    scopedSlots: { customRender: "design_name" },
  },
  {
    title: "SIZE",
    dataIndex: "size_name",
    width: "10%",
    scopedSlots: { customRender: "size_name" },
  },
  {
    title: "REFERENCE CODE",
    dataIndex: "reference_code",
    width: "17%",
    scopedSlots: { customRender: "reference_code" },
  },
  {
    title: "BARCODE",
    dataIndex: "barcode",
    width: "20%",
    scopedSlots: { customRender: "barcode" },
  },
  {
    title: "LANDED COST",
    dataIndex: "landed_cost",
    width: "13%",
    scopedSlots: { customRender: "landed_cost" },
  },
  {
    title: 'ORDERING COST',
    dataIndex: 'ordering_cost',
    width: '15%',
    scopedSlots: { customRender: 'ordering_cost' },
  },
  {
    title: "PRICE",
    dataIndex: "retail_price",
    width: "13%",
    scopedSlots: { customRender: "retail_price" },
  },
  {
    title: "",
    dataIndex: "operation",
    width: "2%",
    scopedSlots: {
      customRender: "operation",
    },
  },
];

export default {
  computed: {
    ...mapState(["endPoint", "profileId"]),
  },
  mounted: async function () {
    var component_x = this;

    let matrixTemp = this.$attrs.matrixTemp;

    let designs = await HTTPRequest.getRequest(
      component_x.endPoint + "designs/get_valid_designs",
      null
    );
    component_x.designOptions = designs;
    component_x.sort(component_x.designOptions);

    let colors = await HTTPRequest.getRequest(
      component_x.endPoint + "colors/get_valid_colors",
      null
    );
    component_x.colorOptions = colors;
    component_x.sort(component_x.colorOptions);

    let size_groups = await HTTPRequest.getRequest(
      component_x.endPoint + "size_groups/get_valid_size_groups",
      null
    );
    component_x.sizeGroupOptions = size_groups;
    component_x.sort(component_x.sizeGroupOptions);

    let sizes = await HTTPRequest.getRequest(
      component_x.endPoint + "sizes/get_valid_sizes",
      null
    );
    component_x.sizeOptions = sizes;
    component_x.sort(component_x.sizeOptions);

    if (component_x.profileId > 0 || matrixTemp.length > 0) {
      var product_matrixes_data = await HTTPRequest.getRequest(
        component_x.endPoint +
          "products?per_page=500&matrix_item=1&matrix_parent_id=" +
          component_x.profileId,
        null
      );
      if (matrixTemp.length > 0) {
        product_matrixes_data.data = matrixTemp;
      }

      component_x.design_id = [];
      component_x.color_id = [];
      component_x.size_id = [];
      component_x.tmpData = [];
      component_x.matrixData = [];
      component_x.recordMatrixData = [];
      component_x.recordData = [];
      component_x.matrixCount = 1;

      let assign_size_group = false;
      if (product_matrixes_data.data[0] != undefined) {
        for (let v = 0; v < product_matrixes_data.data.length; v++) {
          let current_size_id = product_matrixes_data.data[v].size_id;
          if (current_size_id != null && assign_size_group == false) {
            if (
              component_x.sizeOptions.find((x) => x.id == current_size_id) !=
              undefined
            ) {
              component_x.size_group_id = component_x.sizeOptions.find(
                (x) => x.id == current_size_id
              ).size_group_id;
              assign_size_group = true;
            }
          }
        }

        let sizes = await HTTPRequest.getRequest(
          this.endPoint +
            "sizes/get_valid_sizes?size_group_id=" +
            component_x.size_group_id,
          null
        );
        if (sizes != null) {
          component_x.sizeOptions = sizes;
          component_x.sort(component_x.sizeOptions);
        }
      }

      component_x.formSettings = component_x.$attrs.formSettings;
      if (component_x.formSettings.cost_decimal_places != undefined) {
        if (component_x.formSettings.cost_decimal_places == "1") {
          component_x.cost_decimal_places = 2;
        } else {
          component_x.cost_decimal_places = 4;
        }
      }

      if (component_x.formSettings.matrix_format_start != undefined) {
        component_x.matrix_format_start =
          component_x.formSettings.matrix_format_start;
      }

      if (component_x.formSettings.matrix_format_separator != undefined) {
        component_x.matrix_format_separator =
          component_x.formSettings.matrix_format_separator;
      }

      if (component_x.formSettings.matrix_format_end != undefined) {
        component_x.matrix_format_end =
          component_x.formSettings.matrix_format_end;
      }

      product_matrixes_data.data.forEach((element) => {
        let design_name = "";
        if (
          component_x.design_id.find((x) => x == element.design_id) ==
            undefined &&
          component_x.designOptions.find((x) => x.id === element.design_id) !=
            undefined
        ) {
          component_x.design_id.push(element.design_id);
        }
        if (element.design_id != null) {
          if (
            component_x.designOptions.find((x) => x.id === element.design_id) !=
            undefined
          ) {
            design_name = component_x.designOptions.find(
              (x) => x.id === element.design_id
            ).name;
          } else {
            design_name = "Deleted/Inactive Design";
          }
        }

        let color_name = "";
        if (
          component_x.color_id.find((x) => x == element.color_id) ==
            undefined &&
          component_x.colorOptions.find((x) => x.id === element.color_id) !=
            undefined
        ) {
          component_x.color_id.push(element.color_id);
        }
        if (element.color_id != null) {
          if (
            component_x.colorOptions.find((x) => x.id === element.color_id) !=
            undefined
          ) {
            color_name = component_x.colorOptions.find(
              (x) => x.id === element.color_id
            ).name;
          } else {
            color_name = "Deleted/Inactive Color";
          }
        }

        let size_name = "";
        if (
          component_x.size_id.find((x) => x == element.size_id) == undefined &&
          component_x.sizeOptions.find((x) => x.id === element.size_id) !=
            undefined
        ) {
          component_x.size_id.push(element.size_id);
        }

        if (element.size_id != null) {
          if (
            component_x.sizeOptions.find((x) => x.id === element.size_id) !=
            undefined
          ) {
            size_name = component_x.sizeOptions.find(
              (x) => x.id === element.size_id
            ).name;
          } else {
            size_name = "Deleted/Inactive Size";
          }
        }

        let matrix_name = "";
        let combi = "";
        if (design_name != "" && color_name != "" && size_name != "") {
          matrix_name = `${component_x.matrix_format_start}${design_name}${component_x.matrix_format_separator}${color_name}${component_x.matrix_format_separator}${size_name}${component_x.matrix_format_end}`;
          combi = `${element.design_id}-${element.color_id}-${element.size_id}`;
        } else if (
          (design_name != "" && color_name != "") ||
          (design_name != "" && color_name != "") ||
          (color_name != "" && size_name != "")
        ) {
          if (design_name != "" && color_name != "") {
            matrix_name = `${component_x.matrix_format_start}${design_name}${component_x.matrix_format_separator}${color_name}${component_x.matrix_format_end}`;
            combi = `${element.design_id}-${element.color_id}`;
          } else if (design_name != "" && size_name != "") {
            matrix_name = `${component_x.matrix_format_start}${design_name}${component_x.matrix_format_separator}${size_name}${component_x.matrix_format_end}`;
            combi = `${element.design_id}-${element.size_id}`;
          } else if (color_name != "" && size_name != "") {
            matrix_name = `${component_x.matrix_format_start}${color_name}${component_x.matrix_format_separator}${size_name}${component_x.matrix_format_end}`;
            combi = `${element.color_id}-${element.size_id}`;
          }
        } else if (design_name != "" || color_name != "" || size_name != "") {
          if (design_name != "") {
            matrix_name = `${component_x.matrix_format_start}${design_name}${component_x.matrix_format_end}`;
            combi = `${element.design_id}`;
          } else if (color_name != "") {
            matrix_name = `${component_x.matrix_format_start}${color_name}${component_x.matrix_format_end}`;
            combi = `${element.color_id}`;
          } else if (size_name != "") {
            matrix_name = `${component_x.matrix_format_start}${size_name}${component_x.matrix_format_end}`;
            combi = `${element.size_id}`;
          }
        }

        component_x.matrixData.push({
          key: component_x.matrixCount,
          name: matrix_name,
          combi: combi,
          design_id: element.design_id,
          design_name: design_name,
          color_id: element.color_id,
          color_name: color_name,
          size_id: element.size_id,
          size_name: size_name,
          barcode: element.barcode,
          reference_code: element.reference_code,
          landed_cost: Number(element.landed_cost).toFixed(component_x.cost_decimal_places),
          ordering_cost: Number(element.ordering_cost).toFixed(component_x.cost_decimal_places),
          retail_price: Number(element.retail_price).toFixed(2)
        });

        //set holder for database records for matrix data
        component_x.recordMatrixData = component_x.matrixData;
        component_x.matrixCount++;
        component_x.recordMatrixCount = component_x.matrixCount;
      });

      designs = component_x.design_id;
      colors = component_x.color_id;
      sizes = component_x.size_id;

      component_x.rowCount = 1;
      component_x.data = [];
      component_x.selectedRowKeys = [];

      if (designs.length > 0 && colors.length > 0 && sizes.length > 0) {
        designs.forEach((design) => {
          let design_name = component_x.designOptions.find(
            (x) => x.id === design
          ).name;
          colors.forEach((color) => {
            let color_name = component_x.colorOptions.find(
              (x) => x.id === color
            ).name;
            sizes.forEach((size) => {
              let size_name = component_x.sizeOptions.find((x) => x.id === size)
                .name;

              let combi_id = `${design}-${color}-${size}`;
              let combi_name = `${component_x.matrix_format_start}${design_name}${component_x.matrix_format_separator}${color_name}${component_x.matrix_format_separator}${size_name}${component_x.matrix_format_end}`;

              const target = component_x.matrixData.find(
                (item) => item.combi === combi_id
              );
              let old_record = false;
              if (target) {
                component_x.selectedRowKeys.push(component_x.rowCount);
                old_record = true;
              }

              component_x.data.push({
                key: component_x.rowCount,
                name: combi_name,
                combi: combi_id,
                design_id: design,
                design_name: design_name,
                color_id: color,
                color_name: color_name,
                size_id: size,
                size_name: size_name,
                old_record: old_record,
              });

              //set holder for database records including unchecked
              component_x.recordData = component_x.data;
              component_x.rowCount++;
              component_x.recordDataSelected = component_x.selectedRowKeys;
              component_x.recordDataCount = component_x.rowCount;
            });
          });
        });
      } else if (
        (designs.length > 0 && colors.length > 0) ||
        (designs.length > 0 && sizes.length > 0) ||
        (colors.length > 0 && sizes.length > 0)
      ) {
        let first_variants = [];
        let second_variants = [];

        if (designs.length > 0 && colors.length > 0) {
          first_variants = designs;
          second_variants = colors;
        } else if (designs.length > 0 && sizes.length > 0) {
          first_variants = designs;
          second_variants = sizes;
        } else if (colors.length > 0 && sizes.length > 0) {
          first_variants = colors;
          second_variants = sizes;
        }

        first_variants.forEach((first) => {
          let first_name = "";
          if (designs.length > 0 && colors.length > 0) {
            first_name = this.designOptions.find((x) => x.id === first).name;
          } else if (designs.length > 0 && sizes.length > 0) {
            first_name = this.designOptions.find((x) => x.id === first).name;
          } else if (colors.length > 0 && sizes.length > 0) {
            first_name = this.colorOptions.find((x) => x.id === first).name;
          }

          second_variants.forEach((second) => {
            let second_name = "";

            if (designs.length > 0 && colors.length > 0) {
              second_name = this.colorOptions.find((x) => x.id === second).name;
            } else if (designs.length > 0 && sizes.length > 0) {
              second_name = this.sizeOptions.find((x) => x.id === second).name;
            } else if (colors.length > 0 && sizes.length > 0) {
              second_name = this.sizeOptions.find((x) => x.id === second).name;
            }

            let combi_id = `${first}-${second}`;
            let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_separator}${second_name}${this.matrix_format_end}`;

            const target = component_x.matrixData.find(
              (item) => item.combi === combi_id
            );
            let old_record = false;
            if (target) {
              component_x.selectedRowKeys.push(component_x.rowCount);
              old_record = true;
            }

            if (designs.length > 0 && colors.length > 0) {
              component_x.data.push({
                key: component_x.rowCount,
                name: combi_name,
                combi: combi_id,
                design_id: first,
                design_name: first_name,
                color_id: second,
                color_name: second_name,
                old_record: old_record,
              });
            } else if (designs.length > 0 && sizes.length > 0) {
              component_x.data.push({
                key: component_x.rowCount,
                name: combi_name,
                combi: combi_id,
                design_id: first,
                design_name: first_name,
                size_id: second,
                size_name: second_name,
                old_record: old_record,
              });
            } else if (colors.length > 0 && sizes.length > 0) {
              component_x.data.push({
                key: component_x.rowCount,
                name: combi_name,
                combi: combi_id,
                color_id: first,
                color_name: first_name,
                size_id: second,
                size_name: second_name,
                old_record: old_record,
              });
            }

            //set holder for database records including unchecked
            component_x.recordData = component_x.data;
            component_x.rowCount++;
            component_x.recordDataSelected = component_x.selectedRowKeys;
            component_x.recordDataCount = component_x.rowCount;
          });
        });
      } else if (designs.length > 0 || colors.length > 0 || sizes.length > 0) {
        let first_variants = [];
        if (designs.length > 0) {
          first_variants = designs;
        } else if (colors.length > 0) {
          first_variants = colors;
        } else if (sizes.length > 0) {
          first_variants = sizes;
        }

        first_variants.forEach((first) => {
          let first_name = "";
          if (designs.length > 0) {
            first_name = this.designOptions.find((x) => x.id === first).name;
          } else if (colors.length > 0) {
            first_name = this.colorOptions.find((x) => x.id === first).name;
          } else if (sizes.length > 0) {
            first_name = this.sizeOptions.find((x) => x.id === first).name;
          }

          let combi_id = `${first}`;
          let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_end}`;

          const target = component_x.matrixData.find(
            (item) => item.combi === combi_id
          );
          let old_record = false;
          if (target) {
            component_x.selectedRowKeys.push(component_x.rowCount);
            old_record = true;
          }

          if (designs.length > 0) {
            component_x.data.push({
              key: component_x.rowCount,
              name: combi_name,
              combi: combi_id,
              design_id: first,
              design_name: first_name,
              old_record: old_record,
            });
          } else if (colors.length > 0) {
            component_x.data.push({
              key: component_x.rowCount,
              name: combi_name,
              combi: combi_id,
              color_id: first,
              color_name: first_name,
              old_record: old_record,
            });
          } else if (sizes.length > 0) {
            component_x.data.push({
              key: component_x.rowCount,
              name: combi_name,
              combi: combi_id,
              size_id: first,
              size_name: first_name,
              old_record: old_record,
            });
          }

          //set holder for database records including unchecked
          component_x.recordData = component_x.data;
          component_x.rowCount++;
          component_x.recordDataSelected = component_x.selectedRowKeys;
          component_x.recordDataCount = component_x.rowCount;
        });
      }

      component_x.$emit("setMatrixData", component_x.matrixData);
    }
  },
  data() {
    return {
      data: [],
      matrixData: [],
      tmpData: [],
      recordData: [],
      recordMatrixData: [],

      recordDataSelected: [],
      recordDataCount: 1,
      recordMatrixCount: 1,
      columns,
      matrixColumns,
      selectedRowKeys: [],

      rowCount: 1,
      matrixCount: 1,
      active_key: "1",

      product: {},
      designOptions: [],
      design_id: [],
      colorOptions: [],
      color_id: [],
      sizeGroupOptions: [],
      size_group_id: null,
      sizeOptions: [],
      size_id: [],
      current: 1,
      formSettings: [],
      cost_decimal_places: 2,
      matrix_format_start: "(",
      matrix_format_separator: "-",
      matrix_format_end: ")",

      stepTwoSearch: "",
      stepThreeSearch: "",

      errors: [],
      error_count: 0,
    };
  },
  methods: {
    handleChange(value, key, column) {
      const newData = [...this.matrixData];
      const target = newData.filter((item) => key === item.key)[0];
      if (target) {
        target.editable = true;
        target[column] = value;
        delete target.editable;
        this.matrixData = newData;
      }
    },
    onSelectChange(selectedRowKeys) {
      //this contains the check variants
      this.selectedRowKeys = selectedRowKeys;

      //tmp data is for storing data in steps (3) to remember previous data when change happened in previous steps (2)
      this.tmpData = this.matrixData;

      //matrix data is the newly created data for saving
      this.populateMatrix();
    },
    matrixDelete(key) {
      const dataSource = [...this.matrixData];
      this.matrixData = dataSource.filter((item) => item.key !== key);
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      );
    },
    sort(data) {
      data.sort(function (a, b) {
        return a.name.localeCompare(b.name);
      });
    },
    sortBarcode(data) {
      data.sort(function (a, b) {
        return a.barcode.localeCompare(b.barcode);
      });
    },
    getSizes: async function () {
      this.size_id = [];
      let sizes = await HTTPRequest.getRequest(
        this.endPoint +
          "sizes/get_valid_sizes?size_group_id=" +
          this.size_group_id,
        null
      );
      if (sizes != null) {
        this.sizeOptions = sizes;
        this.sort(this.sizeOptions);
      }
    },
    findSearch() {
      //to be continued
    },
    rejectReference(data) {
      let strData = JSON.stringify(data);
      let parseData = JSON.parse(strData);
      return parseData;
    },
    next() {
      if (this.current == 1) {
        let designs = this.design_id;
        let colors = this.color_id;
        let sizes = this.size_id;

        if (this.profileId == 0) {
          this.data = [];
          this.matrixData = [];
          this.selectedRowKeys = [];
          if (designs.length > 0 && colors.length > 0 && sizes.length > 0) {
            designs.forEach((design) => {
              let design_name = this.designOptions.find((x) => x.id === design)
                .name;
              colors.forEach((color) => {
                let color_name = this.colorOptions.find((x) => x.id === color)
                  .name;
                sizes.forEach((size) => {
                  let size_name = this.sizeOptions.find((x) => x.id === size)
                    .name;

                  let combi_id = `${design}-${color}-${size}`;
                  let combi_name = `${this.matrix_format_start}${design_name}${this.matrix_format_separator}${color_name}${this.matrix_format_separator}${size_name}${this.matrix_format_end}`;

                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: design,
                    design_name: design_name,
                    color_id: color,
                    color_name: color_name,
                    size_id: size,
                    size_name: size_name,
                    old_record: false,
                  });

                  this.selectedRowKeys.push(this.rowCount);

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: design,
                    design_name: design_name,
                    color_id: color,
                    color_name: color_name,
                    size_id: size,
                    size_name: size_name,
                    barcode: "",
                    reference_code: "",
                    landed_cost: "",
                    ordering_cost: "",
                    retail_price: "",
                  });

                  this.rowCount++;
                });
              });
            });
          } else if (
            (designs.length > 0 && colors.length > 0) ||
            (designs.length > 0 && sizes.length > 0) ||
            (colors.length > 0 && sizes.length > 0)
          ) {
            let first_variants = [];
            let second_variants = [];

            if (designs.length > 0 && colors.length > 0) {
              first_variants = designs;
              second_variants = colors;
            } else if (designs.length > 0 && sizes.length > 0) {
              first_variants = designs;
              second_variants = sizes;
            } else if (colors.length > 0 && sizes.length > 0) {
              first_variants = colors;
              second_variants = sizes;
            }

            first_variants.forEach((first) => {
              let first_name = "";
              if (designs.length > 0 && colors.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (designs.length > 0 && sizes.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (colors.length > 0 && sizes.length > 0) {
                first_name = this.colorOptions.find((x) => x.id === first).name;
              }

              second_variants.forEach((second) => {
                let second_name = "";

                if (designs.length > 0 && colors.length > 0) {
                  second_name = this.colorOptions.find((x) => x.id === second)
                    .name;
                } else if (designs.length > 0 && sizes.length > 0) {
                  second_name = this.sizeOptions.find((x) => x.id === second)
                    .name;
                } else if (colors.length > 0 && sizes.length > 0) {
                  second_name = this.sizeOptions.find((x) => x.id === second)
                    .name;
                }

                let combi_id = `${first}-${second}`;
                let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_separator}${second_name}${this.matrix_format_end}`;

                if (designs.length > 0 && colors.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    color_id: second,
                    color_name: second_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    color_id: second,
                    color_name: second_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    reference_code: "",
                    retail_price: "",
                  });
                } else if (designs.length > 0 && sizes.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    size_id: second,
                    size_name: second_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    size_id: second,
                    size_name: second_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    retail_price: "",
                    reference_code: "",
                  });
                } else if (colors.length > 0 && sizes.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    color_id: first,
                    color_name: first_name,
                    size_id: second,
                    size_name: second_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    color_id: first,
                    color_name: first_name,
                    size_id: second,
                    size_name: second_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    reference_code: "",
                    retail_price: "",
                  });
                }

                this.selectedRowKeys.push(this.rowCount);
                this.rowCount++;
              });
            });
          } else if (
            designs.length > 0 ||
            colors.length > 0 ||
            sizes.length > 0
          ) {
            let first_variants = [];
            if (designs.length > 0) {
              first_variants = designs;
            } else if (colors.length > 0) {
              first_variants = colors;
            } else if (sizes.length > 0) {
              first_variants = sizes;
            }

            first_variants.forEach((first) => {
              let first_name = "";
              if (designs.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (colors.length > 0) {
                first_name = this.colorOptions.find((x) => x.id === first).name;
              } else if (sizes.length > 0) {
                first_name = this.sizeOptions.find((x) => x.id === first).name;
              }

              let combi_id = `${first}`;
              let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_end}`;

              if (designs.length > 0) {
                this.data.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  design_id: first,
                  design_name: first_name,
                  old_record: false,
                });

                this.matrixData.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  design_id: first,
                  design_name: first_name,
                  barcode: "",
                  landed_cost: "",
                  ordering_cost: "",
                  reference_code: "",
                  retail_price: "",
                });
              } else if (colors.length > 0) {
                this.data.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  color_id: first,
                  color_name: first_name,
                  old_record: false,
                });

                this.matrixData.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  color_id: first,
                  color_name: first_name,
                  barcode: "",
                  landed_cost: "",
                  ordering_cost: "",
                  reference_code: "",
                  retail_price: "",
                });
              } else if (sizes.length > 0) {
                this.data.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  size_id: first,
                  size_name: first_name,
                  old_record: false,
                });

                this.matrixData.push({
                  key: this.rowCount,
                  name: combi_name,
                  combi: combi_id,
                  size_id: first,
                  size_name: first_name,
                  barcode: "",
                  landed_cost: "",
                  ordering_cost: "",
                  reference_code: "",
                  retail_price: "",
                });
              }

              this.selectedRowKeys.push(this.rowCount);
              this.rowCount++;
            });
          }
        } else {
          //for updating matrix

          //it treats this.recordData = this.data vise versa
          this.data = this.rejectReference(this.recordData);
          this.matrixData = this.rejectReference(this.recordMatrixData);
          this.selectedRowKeys = this.rejectReference(this.recordDataSelected);
          this.rowCount = this.rejectReference(this.recordDataCount);
          this.matrixCount = this.rejectReference(this.recordMatrixCount);

          if (designs.length > 0 && colors.length > 0 && sizes.length > 0) {
            designs.forEach((design) => {
              let design_name = this.designOptions.find((x) => x.id === design)
                .name;
              colors.forEach((color) => {
                let color_name = this.colorOptions.find((x) => x.id === color)
                  .name;
                sizes.forEach((size) => {
                  let size_name = this.sizeOptions.find((x) => x.id === size)
                    .name;

                  let combi_id = `${design}-${color}-${size}`;
                  let combi_name = `${this.matrix_format_start}${design_name}${this.matrix_format_separator}${color_name}${this.matrix_format_separator}${size_name}${this.matrix_format_end}`;

                  const target = this.recordData.find(
                    (item) => item.combi === combi_id
                  );
                  if (target == undefined) {
                    this.data.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: design,
                      design_name: design_name,
                      color_id: color,
                      color_name: color_name,
                      size_id: size,
                      size_name: size_name,
                      old_record: false,
                    });

                    this.selectedRowKeys.push(this.rowCount);

                    this.matrixData.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: design,
                      design_name: design_name,
                      color_id: color,
                      color_name: color_name,
                      size_id: size,
                      size_name: size_name,
                      barcode: "",
                      ordering_cost: "",
                      reference_code: "",
                      retail_price: "",
                    });

                    this.rowCount++;
                    this.matrixCount++;
                  }
                });
              });
            });
          } else if (
            (designs.length > 0 && colors.length > 0) ||
            (designs.length > 0 && sizes.length > 0) ||
            (colors.length > 0 && sizes.length > 0)
          ) {
            let first_variants = [];
            let second_variants = [];

            if (designs.length > 0 && colors.length > 0) {
              first_variants = designs;
              second_variants = colors;
            } else if (designs.length > 0 && sizes.length > 0) {
              first_variants = designs;
              second_variants = sizes;
            } else if (colors.length > 0 && sizes.length > 0) {
              first_variants = colors;
              second_variants = sizes;
            }

            first_variants.forEach((first) => {
              let first_name = "";
              if (designs.length > 0 && colors.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (designs.length > 0 && sizes.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (colors.length > 0 && sizes.length > 0) {
                first_name = this.colorOptions.find((x) => x.id === first).name;
              }

              second_variants.forEach((second) => {
                let second_name = "";

                if (designs.length > 0 && colors.length > 0) {
                  second_name = this.colorOptions.find((x) => x.id === second)
                    .name;
                } else if (designs.length > 0 && sizes.length > 0) {
                  second_name = this.sizeOptions.find((x) => x.id === second)
                    .name;
                } else if (colors.length > 0 && sizes.length > 0) {
                  second_name = this.sizeOptions.find((x) => x.id === second)
                    .name;
                }

                let combi_id = `${first}-${second}`;
                let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_separator}${second_name}${this.matrix_format_end}`;

                const target = this.recordData.find(
                  (item) => item.combi === combi_id
                );
                if (target == undefined) {
                  if (designs.length > 0 && colors.length > 0) {
                    this.data.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: first,
                      design_name: first_name,
                      color_id: second,
                      color_name: second_name,
                      old_record: false,
                    });

                    this.matrixData.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: first,
                      design_name: first_name,
                      color_id: second,
                      color_name: second_name,
                      barcode: "",
                      landed_cost: "",
                      ordering_cost: "",
                      reference_code: "",
                      retail_price: "",
                    });
                  } else if (designs.length > 0 && sizes.length > 0) {
                    this.data.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: first,
                      design_name: first_name,
                      size_id: second,
                      size_name: second_name,
                      old_record: false,
                    });

                    this.matrixData.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      design_id: first,
                      design_name: first_name,
                      size_id: second,
                      size_name: second_name,
                      barcode: "",
                      landed_cost: "",
                      ordering_cost: "",
                      reference_code: "",
                      retail_price: "",
                    });
                  } else if (colors.length > 0 && sizes.length > 0) {
                    this.data.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      color_id: first,
                      color_name: first_name,
                      size_id: second,
                      size_name: second_name,
                      old_record: false,
                    });

                    this.matrixData.push({
                      key: this.rowCount,
                      name: combi_name,
                      combi: combi_id,
                      color_id: first,
                      color_name: first_name,
                      size_id: second,
                      size_name: second_name,
                      barcode: "",
                      landed_cost: "",
                      ordering_cost: "",
                      reference_code: "",
                      retail_price: "",
                    });
                  }

                  this.matrixCount++;
                  this.selectedRowKeys.push(this.rowCount);
                  this.rowCount++;
                }
              });
            });
          } else if (
            designs.length > 0 ||
            colors.length > 0 ||
            sizes.length > 0
          ) {
            let first_variants = [];
            if (designs.length > 0) {
              first_variants = designs;
            } else if (colors.length > 0) {
              first_variants = colors;
            } else if (sizes.length > 0) {
              first_variants = sizes;
            }

            first_variants.forEach((first) => {
              let first_name = "";
              if (designs.length > 0) {
                first_name = this.designOptions.find((x) => x.id === first)
                  .name;
              } else if (colors.length > 0) {
                first_name = this.colorOptions.find((x) => x.id === first).name;
              } else if (sizes.length > 0) {
                first_name = this.sizeOptions.find((x) => x.id === first).name;
              }

              let combi_id = `${first}`;
              let combi_name = `${this.matrix_format_start}${first_name}${this.matrix_format_end}`;

              const target = this.recordData.find(
                (item) => item.combi === combi_id
              );
              if (target == undefined) {
                if (designs.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    design_id: first,
                    design_name: first_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    reference_code: "",
                    retail_price: "",
                  });
                } else if (colors.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    color_id: first,
                    color_name: first_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    color_id: first,
                    color_name: first_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    reference_code: "",
                    retail_price: "",
                  });
                } else if (sizes.length > 0) {
                  this.data.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    size_id: first,
                    size_name: first_name,
                    old_record: false,
                  });

                  this.matrixData.push({
                    key: this.rowCount,
                    name: combi_name,
                    combi: combi_id,
                    size_id: first,
                    size_name: first_name,
                    barcode: "",
                    landed_cost: "",
                    ordering_cost: "",
                    reference_code: "",
                    retail_price: "",
                  });
                }

                this.selectedRowKeys.push(this.rowCount);
                this.rowCount++;
                this.matrixCount++;
              }
            });
          }
        }
      } else if (this.current == 2) {
        this.tmpData = this.matrixData;
        this.populateMatrix();
      }

      this.current++;
      this.active_key = `${this.current}`;
    },
    prev() {
      this.current--;
      this.active_key = `${this.current}`;
    },
    done() {
      let results = "";
      let prev_barcode = "";
      let blank_barcode = false;
      let same_with_parent = false;

      this.errors = [];
      this.error_count = 0;

      let toBeCheckData = this.rejectReference(this.matrixData);
      this.sortBarcode(toBeCheckData);

      this.product = this.$attrs.product;

      for (let i = 0; i < toBeCheckData.length - 1; i++) {
        if (i != toBeCheckData.length - 1) {
          if (
            toBeCheckData[i].barcode == "" ||
            toBeCheckData[i].barcode == undefined
          ) {
            blank_barcode = true;
          } else if (toBeCheckData[i + 1].barcode == toBeCheckData[i].barcode) {
            if (prev_barcode != toBeCheckData[i].barcode) {
              if (results == "") {
                results += toBeCheckData[i].barcode;
              } else {
                results += ", " + toBeCheckData[i].barcode;
              }
              this.error_count += 1;
            }

            prev_barcode = toBeCheckData[i].barcode;
          } else if (this.product.barcode == toBeCheckData[i].barcode) {
            same_with_parent = true;
            this.error_count += 1;
          }
        }
      }

      if (this.error_count == 0) {
        this.$emit("setMatrixData", this.matrixData);
        this.errors.push("Success! You created variants for this product");
      } else {
        if (results != "") {
          this.errors.push("Barcode " + results + " is/are already exist");
        }

        if (same_with_parent == true) {
          this.errors.push(
            `Matrix Variants: Barcode ${this.product.barcode} is the same with matrix parent`
          );
        }
      }
    },
    callback(val) {
      let next = parseInt(this.current) + 1;
      if (next == val) {
        this.next();
      } else {
        this.current = val;
        this.active_key = `${this.current}`;
      }
    },
    populateMatrix() {
      this.matrixData = [];

      this.selectedRowKeys.forEach((key) => {
        const checkData = this.data.find(item => item.key === key);
        if (!checkData) return;

        // Check if combination already exists in tmpData
        const existingMatrix = this.tmpData.find(
          item => item.combi === checkData.combi
        );

        if (existingMatrix) {
          this.matrixData.push({ ...existingMatrix });
          return;
        }

        // Collect available attributes
        const parts = [];
        const ids = [];

        if (checkData.design_name) {
          parts.push(checkData.design_name);
          ids.push(checkData.design_id);
        }

        if (checkData.color_name) {
          parts.push(checkData.color_name);
          ids.push(checkData.color_id);
        }

        if (checkData.size_name) {
          parts.push(checkData.size_name);
          ids.push(checkData.size_id);
        }

        // Build matrix name and combination string
        const matrix_name = parts.length
          ? `${this.matrix_format_start}${parts.join(this.matrix_format_separator)}${this.matrix_format_end}`
          : "";

        const combi = ids.join("-");

        // Push new matrix entry
        this.matrixData.push({
          key: this.matrixCount,
          old_record: checkData.old_record,
          name: matrix_name,
          combi: combi,
          design_id: checkData.design_id,
          design_name: checkData.design_name,
          color_id: checkData.color_id,
          color_name: checkData.color_name,
          size_id: checkData.size_id,
          size_name: checkData.size_name,
          barcode: "",
          landed_cost: "",
          ordering_cost: "",
          reference_code: "",
          retail_price: ""
        });

        this.matrixCount++;
      });
    },
    populateMatrixBak() {
      this.matrixData = [];
      this.selectedRowKeys.forEach((element) => {
        let checkData = this.data.find((x) => x.key === element);
        let matrix = this.tmpData.find((x) => x.combi === checkData.combi);

        if (matrix) {
          this.matrixData.push({
            key: matrix.key,
            name: matrix.name,
            combi: matrix.combi,
            design_id: matrix.design_id,
            design_name: matrix.design_name,
            color_id: matrix.color_id,
            color_name: matrix.color_name,
            size_id: matrix.size_id,
            size_name: matrix.size_name,
            barcode: matrix.barcode,
            reference_code: matrix.reference_code,
            landed_cost: matrix.landed_cost,
            ordering_cost: matrix.ordering_cost,
            retail_price: matrix.retail_price,
          });
        } else {
          let matrix_name = "";
          let combi = "";

          if (
            checkData.design_name != "" &&
            checkData.color_name != "" &&
            checkData.size_name != ""
          ) {
            matrix_name = `${this.matrix_format_start}${checkData.design_name}${this.matrix_format_separator}${checkData.color_name}${this.matrix_format_separator}${checkData.size_name}${this.matrix_format_end}`;
            combi = `${checkData.design_id}-${checkData.color_id}-${checkData.size_id}`;
          } else if (
            (checkData.design_name != "" && checkData.color_name != "") ||
            (checkData.design_name != "" && checkData.color_name != "") ||
            (checkData.color_name != "" && checkData.size_name != "")
          ) {
            if (checkData.design_name != "" && checkData.color_name != "") {
              matrix_name = `${this.matrix_format_start}${checkData.design_name}${this.matrix_format_separator}${checkData.color_name}${this.matrix_format_end}`;
              combi = `${checkData.design_id}-${checkData.color_id}`;
            } else if (
              checkData.design_name != "" &&
              checkData.size_name != ""
            ) {
              matrix_name = `${this.matrix_format_start}${checkData.design_name}${this.matrix_format_separator}${checkData.size_name}${this.matrix_format_end}`;
              combi = `${checkData.design_id}-${checkData.size_id}`;
            } else if (
              checkData.color_name != "" &&
              checkData.size_name != ""
            ) {
              matrix_name = `${this.matrix_format_start}${checkData.color_name}${this.matrix_format_separator}${checkData.size_name}${this.matrix_format_end}`;
              combi = `${checkData.color_id}-${checkData.size_id}`;
            }
          } else if (
            checkData.design_name != "" ||
            checkData.color_name != "" ||
            checkData.size_name != ""
          ) {
            if (checkData.design_name != "" && checkData.color_name != "") {
              matrix_name = `${this.matrix_format_start}${checkData.design_name}${this.matrix_format_end}`;
              combi = `${checkData.design_id}`;
            } else if (
              checkData.design_name != "" &&
              checkData.size_name != ""
            ) {
              matrix_name = `${this.matrix_format_start}${checkData.color_name}${this.matrix_format_end}`;
              combi = `${checkData.color_id}`;
            } else if (
              checkData.color_name != "" &&
              checkData.size_name != ""
            ) {
              matrix_name = `${this.matrix_format_start}${checkData.size_name}${this.matrix_format_end}`;
              combi = `${checkData.size_id}`;
            }
          }

          this.matrixData.push({
            key: this.matrixCount,
            name: matrix_name,
            combi: combi,
            design_id: checkData.design_id,
            design_name: checkData.design_name,
            color_id: checkData.color_id,
            color_name: checkData.color_name,
            size_id: checkData.size_id,
            size_name: checkData.size_name,
            barcode: "",
            landed_cost: "",
            ordering_cost: "",
            reference_code: "",
            retail_price: ""
          });

          this.matrixCount++;
        }
      });
    },
  },
};
</script>
<style scoped>
.numberCircle {
  display: inline-block;
  border-radius: 50%;
  border: 2px solid;
  font-size: 18px;
  background: #65cb8f;
  border: #65cb8f;
  color: #ffffff;
}

.numberCircle:before,
.numberCircle:after {
  content: "\200B";
  display: inline-block;
  line-height: 0px;
  padding-top: 50%;
  padding-bottom: 50%;
}

.numberCircle:before {
  padding-left: 8px;
}
.numberCircle:after {
  padding-right: 8px;
}
.editable-row-operations a {
  margin-right: 8px;
}
</style>
